import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';

import styles from '../../../css/markdown.module.css';

import AsideLearn from '@components/AsideLearn';
import ContainerSidebar from '@components/ContainerSidebar';
import CTA from '@components/CTA';
import CustomHead from '@components/CustomHead';
import Footer from '@components/FooterNew';
import Header from '@components/HeaderNew';
import Heading from '@components/Heading';
import { getSqlCategoryPage, getSqlTutorials } from '@utils/contentfulClient';

const TutorialPage = ({ tutorials }) => {
  const router = useRouter();
  const parsedTutorials = tutorials.map((tut) => tut.fields);
  const category = parsedTutorials[0]?.category[0];
  const sortedTutorials = {};

  parsedTutorials.forEach((tutorial) => {
    const type = tutorial.type[0];
    sortedTutorials[type] = sortedTutorials[type] || [];
    sortedTutorials[type].push(tutorial);
  });

  // Create an object which will serve as the order template
  const objectOrder = {
    Basics: null,
    'Database Management': null,
    'Dates and Times': null,
    Analysis: null,
  };

  const orderedTutoritals = Object.assign(objectOrder, sortedTutorials);

  return (
    <>
      <CustomHead
        meta={{
          title: `${category} - PopSQL`,
          image: `${process.env.NEXT_PUBLIC_ASSET_HOST}/static/images/og/learn.jpg`,
        }}
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_WEB_HOST}${router.asPath}`,
          image: `${process.env.NEXT_PUBLIC_ASSET_HOST}/static/images/og/learn.jpg`,
        }}
      />
      <main>
        <Header />
        <ContainerSidebar
          type="learn"
          asideComponent={<AsideLearn withSectionsOnly />}
          dbName={category === 'PostgresSQL' ? 'postgresql' : category}
        >
          <div className={`${styles.popsql__markdown}`}>
            <Heading Tag="h1" size="sm" customStyle={{ fontWeight: '600' }}>
              {category}
            </Heading>

            {Object.keys(orderedTutoritals).map((type) => (
              <div key={type}>
                <p
                  className="text-gray-500 mt-2 mb-1 text-xs font-semibold uppercase tracking-wider"
                  style={{ margin: '12px 0 8px', fontSize: '13px' }}
                >
                  {type}
                </p>
                <div className="mb-8 font-inter">
                  {orderedTutoritals[type]?.map((tutorial, i) => (
                    <Link
                      key={`post-link-${i}`}
                      href={tutorial.slug}
                      as={tutorial.slug}
                    >
                      <a className="block mb-2 font-inter">{tutorial.title}</a>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ContainerSidebar>
        <CTA />
      </main>
      <Footer />
    </>
  );
};

export async function getStaticPaths() {
  const tutorials = await getSqlTutorials();
  const paths = [];
  tutorials.forEach((t) => {
    if (t.fields.slug) {
      const category = t.fields.category[0]?.split(' ').join('-');
      paths.push(`/learn-sql/${category}`);
    }
  });

  return {
    paths,
    fallback: 'blocking',
  };
}

export async function getStaticProps(ctx) {
  const { id } = ctx.params;
  const tutorials = await getSqlCategoryPage(id);

  return {
    props: {
      tutorials,
    },
  };
}

export default TutorialPage;
